<template>
  <div class="group relative" @click="handleWrapperClickEvent">
    <div class="relative h-full" :class="containerClasses">
      <div ref="container" class="keen-slider absolute flex bg-bgr h-full">
        <div
          v-for="({ id, alt, title, src }, index) in slides"
          :key="id"
          class="keen-slider__slide lazy__slide relative h-full !min-w-full"
        >
          <img
            :src="loaded[index] ? src : ''"
            :alt="alt"
            :title="title"
            :class="[
              coverFull ? 'w-full' : 'w-[inherit]',
              imageHeight ? `h-[${imageHeight}]` : 'h-full',
            ]"
            class="m-auto object-cover aspect-[4/3]"
          />
          <div
            class="absolute inset-0 pointer-events-none *:pointer-events-auto"
          >
            <slot name="overlay" :index="index" />
          </div>
        </div>
      </div>
      <template v-if="!$screen.isSmall">
        <div
          v-for="(isActive, direction) in slideButtons"
          :key="direction"
          class="absolute top-0 h-full"
          :class="`${direction}-0`"
        >
          <slot
            v-if="isActive"
            :name="`${direction}-button`"
            :click="(event) => onSlideButtonClick(direction, event)"
          >
            <div
              class="flex h-full items-center"
              :class="[
                direction === 'left' ? 'pl-4' : 'pr-4',
                { '!pointer-events-none': inDragging },
              ]"
              data-keen-slider-clickable
              @click.stop="onSlideButtonClick(direction, $event)"
              @mouseover="onSlideChangeIntent(direction)"
            >
              <button
                v-if="modern"
                class="p-2 rounded-full z-1 bg-bgr hover:bg-bgr-100 transition focus:bg-bgr-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-thm-hover opacity-0 group-hover:opacity-80 focus:opacity-80"
                @focus="onSlideChangeIntent(direction)"
              >
                <WebccIcon
                  :name="`site/arrow-${direction}`"
                  class="h-4 w-4 text-txt"
                  filled
                />
              </button>
              <button
                v-else
                class="block p-1 rounded-lg z-1 bg-bgr bg-opacity-80 text-txt backdrop-blur-md transition-all opacity-0 duration-200 hover:scale-105 hover:bg-opacity-90 hover:shadow-md active:scale-100 group-hover:opacity-100"
              >
                <WebccIcon
                  :name="`site/chevron-${direction}`"
                  class="h-6 w-6 text-txt"
                  filled
                />
              </button>
            </div>
          </slot>
        </div>
      </template>
      <div class="absolute inset-0 pointer-events-none *:pointer-events-auto">
        <slot name="container-overlay" />
      </div>
    </div>
    <div
      v-if="isMoreThenOneSlide && dots !== 'none'"
      class="flex w-full justify-center items-center pointer-events-none"
      :class="dots === 'internal' ? 'gap-1 absolute bottom-2' : 'gap-2 p-4'"
    >
      <span
        v-for="index in paginationDots"
        :key="index"
        class="w-2 h-2 bg-bgr rounded-full transition-colors duration-300 pointer-events-auto"
        :class="[
          current === index ? dotsStyle.current : dotsStyle.default,
          { '!pointer-events-none': inDragging },
        ]"
        data-keen-slider-clickable
        @click.stop="slideTo(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/vue.es'

type DotsDisplayMode = 'external' | 'internal' | 'none'

const dotsStyle = computed(() => {
  return props.dots === 'internal'
    ? { current: 'opacity-100 w-2.5 h-2.5', default: 'opacity-60' }
    : { current: 'bg-gray-700' }
})

const props = withDefaults(
  defineProps<{
    images: MediaImage[]
    containerClasses?: string
    coverFull?: boolean
    initial?: number
    dots?: DotsDisplayMode
    modern?: boolean
    loop?: boolean
    imageHeight?: string
  }>(),
  {
    containerClasses: undefined,
    coverFull: false,
    initial: 0,
    dots: 'none',
    modern: false,
    loop: true,
    imageHeight: undefined,
  },
)

const emit = defineEmits<{
  slideChange: [index: number]
  slideClick: [index: number]
  slideChangeIntent: [direction: ScrollDirectionHorizontal]
  slideButtonClick: [direction: ScrollDirectionHorizontal]
}>()

defineSlots<{
  'overlay'(props: { index: number }): unknown
  'left-button'(props: { click(event: MouseEvent): void }): unknown
  'right-button'(props: { click(event: MouseEvent): void }): unknown
  'container-overlay'(): unknown
}>()

const current = ref(props.initial)
const loaded = ref<boolean[]>([true, true])
const slides = computed(() => props.images)

const isMoreThenOneSlide = computed(() => slides.value.length > 1)

const slideButtons = computed(() => ({
  left: isMoreThenOneSlide.value && (props.loop || current.value > 0),
  right:
    isMoreThenOneSlide.value &&
    (props.loop || current.value < slides.value.length - 1),
}))

watch(slides, () => nextTick(() => slider.value?.update()))

const startPosition = ref(0)
const currentReachedDelta = ref(0)
const deltaClickThreshold = 5
const inTouching = ref(false)
const inDragging = computed(
  () => currentReachedDelta.value > deltaClickThreshold,
)
const deltaClearTimeout = ref<ReturnType<typeof setTimeout>>()

const [container, slider] = useKeenSlider({
  loop: props.loop,
  initial: current.value,
  defaultAnimation: { duration: 800 },
  dragSpeed: 0.6,
  dragStarted({ track: { details } }) {
    clearTimeout(deltaClearTimeout.value)
    currentReachedDelta.value = 0
    startPosition.value = details.position
    onSlideChangeIntent(details.abs - current.value > 0 ? 'right' : 'left')
  },
  dragged({ track: { details } }) {
    const absDelta = Math.abs(details.position - startPosition.value) * 100
    if (currentReachedDelta.value < absDelta) {
      currentReachedDelta.value = absDelta
    }
  },
  dragEnded({ track: { details } }) {
    if (!inTouching.value && currentReachedDelta.value < deltaClickThreshold) {
      onSlideClick(details.rel)
    }
    deltaClearTimeout.value = setTimeout(
      () => (currentReachedDelta.value = 0),
      300,
    )
  },
  slideChanged({ track: { details } }) {
    current.value = positiveMod(details.rel, slides.value.length)
    loadImagesAround(current.value)
    emit('slideChange', details.rel)
  },
})

useEventListener(container, 'touchstart', () => (inTouching.value = true), {
  passive: true,
})

useEventListener(container, 'touchend', () => (inTouching.value = false), {
  passive: true,
})

function handleWrapperClickEvent(event: MouseEvent) {
  if (!inDragging.value) return
  event.stopImmediatePropagation()
}

/**
 * Loads the given and surrounding images for smooth sliding.
 */
function loadImagesAround(index: number) {
  ;[index - 1, index, index + 1].forEach(loadImageAt)
}

/**
 * Marks the image at the given index as `loaded` to trigger its actual load and rendering.
 */
function loadImageAt(index: number) {
  const relativeIndex = index % slides.value.length
  const image = slides.value.at(relativeIndex)
  if (image) loaded.value[relativeIndex] = true
}

onBeforeUnmount(() => {
  slider.value?.destroy()
})

function slideTo(index: number) {
  slider.value?.moveToIdx(index)
}

function onSlideButtonClick(
  direction: ScrollDirectionHorizontal,
  event: MouseEvent,
) {
  if (inDragging.value) {
    event.stopImmediatePropagation()
    return
  }
  const slideFn = direction === 'left' ? slider.value?.prev : slider.value?.next
  slideFn?.()
  emit('slideButtonClick', direction)
}

function onSlideChangeIntent(direction: ScrollDirectionHorizontal) {
  emit('slideChangeIntent', direction)
}

/**
 * Quickfix: For some reason KeenSlider triggers drag events twice,
 * to prevent firing click event twice here is temporal fix using timeout.
 * The timeout should be more than 290ms as in iOS it triggers with a delay 200-290ms.
 */
const NEXT_CLICK_TIMEOUT = 500
const lastClickTime = ref(0)

function onSlideClick(index: number) {
  const now = Date.now()
  if (now - lastClickTime.value < NEXT_CLICK_TIMEOUT) {
    return
  }
  lastClickTime.value = now
  emit('slideClick', index)
}

const maxDots = 5
const paginationDots = computed(() => {
  if (props.dots === 'external')
    return Array.from({ length: slides.value.length }, (_, i) => i)
  const total = slides.value.length
  const half = Math.ceil(maxDots / 2)

  if (current.value < half) {
    return Array.from({ length: Math.min(maxDots, total) }, (_, i) => i)
  }
  if (current.value > total - half) {
    return Array.from(
      { length: Math.min(maxDots, total) },
      (_, i) => total - maxDots + i,
    )
  }
  return Array.from({ length: maxDots }, (_, i) => current.value + 1 + i - half)
})
</script>
